import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, AlertTitle, Box, Grid, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

import {
  COLOR_FIRST_BID,
  COLOR_FIRST_COMPLETED_JOB,
  SUPPLIER_ACTIVATION_FOOTNOTES,
  SUPPLIER_ACTIVATION_INFO,
} from 'components/reports/Metrics/constants/SupplierActivation'
import { useGetSupplierActivationQuery } from 'generated/graphql'
import { cohortSpan } from 'helpers/supplierActivation.helpers'

import { SupplierActivationCards } from './SupplierActivationCards'
import { SupplierActivationCurveCard } from './SupplierActivationCurveCard'
import { SupplierActivationExportButton } from './SupplierActivationExportButton'
import { SupplierActivationFiltersForm } from './SupplierActivationFilters'
import { SupplierActivationFootnote } from './SupplierActivationFootnote'
import { useSupplierActivationQueryInput } from './useSupplierActivationQueryInput'

export const SupplierActivationContent = () => {
  const { input, hasValidRange } = useSupplierActivationQueryInput()

  const { data, loading } = useGetSupplierActivationQuery({
    variables: { input },
    skip: !hasValidRange,
  })

  const activation = data?.supplierActivation

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', lg: 'flex-end' }}
        justifyContent='space-between'
        mb={3}>
        <Box>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='h5' fontWeight={700} sx={{ color: 'primary.main' }}>
              Supplier Creation
            </Typography>
            <Tooltip
              title={SUPPLIER_ACTIVATION_INFO}
              slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
            </Tooltip>
          </Stack>
          <Typography variant='body2' color='text.secondary'>
            Analyse how quickly newly created drivers engage with the platform and complete their
            first job.
          </Typography>
        </Box>

        <Stack
          direction='row'
          spacing={2}
          useFlexGap
          flexWrap='wrap'
          alignItems='flex-end'
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
          <SupplierActivationFiltersForm />
          <SupplierActivationExportButton />
        </Stack>
      </Stack>

      <Typography variant='subtitle1' fontWeight={600} mb={0.5}>
        Creation cohort overview
      </Typography>
      <Typography variant='body2' color='text.secondary' mb={2}>
        Drivers with the Owner or Transport Controller role created within the selected time period.
        Only OnePoint suppliers are included. Courier Exchange suppliers are excluded because their
        offers are not recorded as bids and would otherwise appear as never having placed a bid.
      </Typography>

      <SupplierActivationCards data={activation} loading={loading} />

      <SupplierActivationFootnote />

      {!loading && activation?.cohortSize === 0 && (
        <Alert severity='info' sx={{ mt: 2 }}>
          No drivers were created during the selected period, so there is nothing to measure. Try a
          longer period.
        </Alert>
      )}

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <SupplierActivationCurveCard
            title='Time from creation to first bid'
            subtitle='Cumulative % of created drivers who have placed at least one bid.'
            info='The percentage at each point includes only drivers who have reached that number of days since account creation. Drivers who have not yet reached that point are excluded from the calculation.'
            points={activation?.firstBid.points ?? []}
            color={COLOR_FIRST_BID}
            milestoneVerb='placed their first bid'
            loading={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <SupplierActivationCurveCard
            title='Time from creation to first completed job'
            subtitle='Cumulative % of created drivers who have completed at least one job.'
            info='Each point shows the percentage of eligible drivers who had completed their first job by that time. A driver becomes eligible for each point once their account has existed for the required number of days.'
            points={activation?.firstCompletedJob.points ?? []}
            color={COLOR_FIRST_COMPLETED_JOB}
            milestoneVerb='completed their first job'
            loading={loading}
          />
        </Grid>
      </Grid>

      {activation?.cohortStart && activation?.cohortEnd && (
        <Stack direction='row' spacing={1} alignItems='center' mt={2}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          <Typography variant='caption' color='text.secondary'>
            {cohortSpan(activation.cohortSize, activation.cohortStart, activation.cohortEnd)}
          </Typography>
        </Stack>
      )}

      <Alert severity='info' icon={<InfoOutlinedIcon />} sx={{ mt: 3 }}>
        <AlertTitle sx={{ fontWeight: 600 }}>How to read these charts</AlertTitle>
        These charts show the cumulative percentage of newly created drivers who reach each
        milestone within the specified number of days. At each point, only drivers whose accounts
        have existed for at least that many days are included. Drivers who have not yet reached that
        timeframe are excluded from the calculation.
      </Alert>

      <Box
        sx={{
          mt: 2,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: alpha(COLOR_FIRST_BID, 0.04),
          border: 1,
          borderColor: alpha(COLOR_FIRST_BID, 0.2),
        }}>
        <Stack direction='row' spacing={1.25} alignItems='flex-start'>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: COLOR_FIRST_BID, mt: 0.25 }} />
          <Box>
            {SUPPLIER_ACTIVATION_FOOTNOTES.map((line) => (
              <Typography
                key={line}
                variant='caption'
                color='text.secondary'
                display='block'
                sx={{ lineHeight: 1.7 }}>
                {line}
              </Typography>
            ))}
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}
